import React, { useState, useEffect } from 'react';
import { 
  Activity, DollarSign, Cpu, TrendingUp, Server, 
  RefreshCw, ChevronDown, ChevronUp, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AICostDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [projection, setProjection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [userCount, setUserCount] = useState(100);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('rooftrack_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [summaryRes, platformsRes, projectionRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ai-usage/summary`, { headers }),
        axios.get(`${BACKEND_URL}/api/ai-usage/multi-platform-summary`, { headers }),
        axios.get(`${BACKEND_URL}/api/ai-usage/projection?users=${userCount}`, { headers })
      ]);
      
      setSummary(summaryRes.data);
      setPlatforms(platformsRes.data);
      setProjection(projectionRes.data);
    } catch (err) {
      console.error('Failed to fetch AI usage data:', err);
      setError('Failed to load AI usage data. Make sure you have admin access.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Refetch projection when user count changes
    const fetchProjection = async () => {
      try {
        const token = localStorage.getItem('rooftrack_token');
        const res = await axios.get(
          `${BACKEND_URL}/api/ai-usage/projection?users=${userCount}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProjection(res.data);
      } catch (err) {
        console.error('Failed to fetch projection:', err);
      }
    };
    
    if (!isLoading) {
      fetchProjection();
    }
  }, [userCount]);

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-blue-400 animate-spin mr-2" />
          <span className="text-slate-400">Loading AI usage data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center text-red-400">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const recommendation = platforms?.recommendation || {};

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">AI & Platform Costs</h3>
          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
            Gemini Flash
          </span>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={fetchData}
          className="text-slate-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Main Stats Grid */}
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Today's Calls */}
        <div className="bg-slate-700/50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <Activity className="w-4 h-4" />
            Today
          </div>
          <div className="text-2xl font-bold text-white">
            {summary?.today?.calls || 0}
          </div>
          <div className="text-xs text-slate-500">AI calls</div>
        </div>

        {/* This Month's Calls */}
        <div className="bg-slate-700/50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            This Month
          </div>
          <div className="text-2xl font-bold text-white">
            {summary?.this_month?.calls || 0}
          </div>
          <div className="text-xs text-slate-500">AI calls</div>
        </div>

        {/* Total Credits */}
        <div className="bg-slate-700/50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <Server className="w-4 h-4" />
            Credits Used
          </div>
          <div className="text-2xl font-bold text-white">
            {platforms?.totals?.grand_total_credits?.toFixed(1) || '50'}
          </div>
          <div className="text-xs text-slate-500">this month</div>
        </div>

        {/* Cost Savings */}
        <div className="bg-slate-700/50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <DollarSign className="w-4 h-4" />
            Est. Cost
          </div>
          <div className="text-2xl font-bold text-green-400">
            ${summary?.this_month?.cost_usd?.toFixed(4) || '0.00'}
          </div>
          <div className="text-xs text-green-500">70% savings vs GPT-4</div>
        </div>
      </div>

      {/* Plan Recommendation */}
      <div className="px-4 pb-4">
        <div className={`rounded-lg p-3 flex items-center justify-between ${
          recommendation.plan === 'Free' ? 'bg-green-500/10 border border-green-500/30' :
          recommendation.plan === 'Pro' ? 'bg-blue-500/10 border border-blue-500/30' :
          'bg-purple-500/10 border border-purple-500/30'
        }`}>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-5 h-5 ${
              recommendation.plan === 'Free' ? 'text-green-400' :
              recommendation.plan === 'Pro' ? 'text-blue-400' :
              'text-purple-400'
            }`} />
            <div>
              <div className="text-white font-medium">
                Recommended: {recommendation.plan || 'Free'} Plan
              </div>
              <div className="text-sm text-slate-400">
                {recommendation.reason || 'Your usage fits within the free tier'}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-white">
              ${recommendation.monthly_cost || 0}/mo
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Details */}
      <div className="border-t border-slate-700">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full px-4 py-3 flex items-center justify-between text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
        >
          <span className="text-sm">View detailed breakdown</span>
          {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showDetails && (
          <div className="px-4 pb-4 space-y-4">
            {/* Platform Breakdown */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2">Platform Breakdown</h4>
              <div className="bg-slate-900 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-3 text-slate-400 font-medium">Platform</th>
                      <th className="text-right p-3 text-slate-400 font-medium">AI Calls</th>
                      <th className="text-right p-3 text-slate-400 font-medium">AI Credits</th>
                      <th className="text-right p-3 text-slate-400 font-medium">Deploy</th>
                      <th className="text-right p-3 text-slate-400 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platforms?.platforms?.map((p, i) => (
                      <tr key={i} className="border-b border-slate-800">
                        <td className="p-3 text-white">{p.name}</td>
                        <td className="p-3 text-right text-slate-300">{p.ai_calls}</td>
                        <td className="p-3 text-right text-slate-300">{p.ai_credits?.toFixed(2)}</td>
                        <td className="p-3 text-right text-slate-300">{p.deployment_credits}</td>
                        <td className="p-3 text-right text-white font-medium">{p.total_credits?.toFixed(1)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-800">
                      <td className="p-3 text-white font-medium">Total</td>
                      <td className="p-3 text-right text-slate-300">-</td>
                      <td className="p-3 text-right text-slate-300">
                        {platforms?.totals?.total_ai_credits?.toFixed(2)}
                      </td>
                      <td className="p-3 text-right text-slate-300">
                        {platforms?.totals?.total_deployment_credits}
                      </td>
                      <td className="p-3 text-right text-white font-bold">
                        {platforms?.totals?.grand_total_credits?.toFixed(1)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Cost Projection */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2">Cost Projection by Users</h4>
              <div className="flex items-center gap-4 mb-3">
                <label className="text-slate-400 text-sm">Active Users:</label>
                <input
                  type="range"
                  min="10"
                  max="1000"
                  step="10"
                  value={userCount}
                  onChange={(e) => setUserCount(parseInt(e.target.value))}
                  className="flex-1 accent-blue-500"
                />
                <span className="text-white font-medium w-16 text-right">{userCount}</span>
              </div>
              
              {projection?.projection && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-900 rounded-lg p-3">
                    <div className="text-slate-400 text-xs">Monthly Calls</div>
                    <div className="text-white font-medium">
                      {projection.projection.monthly_calls?.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-slate-900 rounded-lg p-3">
                    <div className="text-slate-400 text-xs">AI Credits</div>
                    <div className="text-white font-medium">
                      {projection.projection.ai_credits?.toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-slate-900 rounded-lg p-3">
                    <div className="text-slate-400 text-xs">Total Credits/Mo</div>
                    <div className="text-white font-medium">
                      {projection.projection.total_credits_monthly?.toFixed(1)}
                    </div>
                  </div>
                  <div className="bg-slate-900 rounded-lg p-3">
                    <div className="text-slate-400 text-xs">Annual Credits</div>
                    <div className="text-white font-medium">
                      {projection.projection.total_credits_annual?.toFixed(0)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Emergent Plan Comparison */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2">Emergent Plans</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={`rounded-lg p-3 border ${
                  recommendation.plan === 'Free' ? 'bg-green-500/10 border-green-500/50' : 'bg-slate-900 border-slate-700'
                }`}>
                  <div className="text-slate-400 text-xs">Free</div>
                  <div className="text-white font-medium">50 credits</div>
                  <div className="text-green-400 text-sm">$0/mo</div>
                </div>
                <div className={`rounded-lg p-3 border ${
                  recommendation.plan === 'Pro' ? 'bg-blue-500/10 border-blue-500/50' : 'bg-slate-900 border-slate-700'
                }`}>
                  <div className="text-slate-400 text-xs">Pro</div>
                  <div className="text-white font-medium">500 credits</div>
                  <div className="text-blue-400 text-sm">$20/mo</div>
                </div>
                <div className={`rounded-lg p-3 border ${
                  recommendation.plan === 'Team' ? 'bg-purple-500/10 border-purple-500/50' : 'bg-slate-900 border-slate-700'
                }`}>
                  <div className="text-slate-400 text-xs">Team</div>
                  <div className="text-white font-medium">1,500 credits</div>
                  <div className="text-purple-400 text-sm">$50/mo</div>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <div className="text-slate-400 text-xs">Enterprise</div>
                  <div className="text-white font-medium">Custom</div>
                  <div className="text-slate-400 text-sm">Contact</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICostDashboard;
